<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Real Estate
              <tooltip
                v-if="realEstateTooltipText !== ''"
                :content="realEstateTooltipText"
                :tooltip-options="{
                  classes: ['large'],
                }"
              />
            </h2>
            <div class="subtitle is-2">
              <div v-html="realEstateText ? realEstateText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Real Estate</strong> to report.
            </checkbox-adv>
          </div>
        </div>
        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-real-estate"
            tag="form"
          >
            <div class="columns is-multiline">
              <div class="column is-5">
                <dropdown-vee
                  id="location-real-estate"
                  v-model="newItem.locationRealEstate"
                  :options="realEstateLocations"
                  placeholder="Location of Real Estate Transactions"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-7">
                <textbox-vee
                  id="description"
                  v-model="newItem.nature"
                  placeholder="Real Estate Description"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox-vee
                  id="street-address"
                  v-model="newItem.streetAddress"
                  placeholder="Real Estate Address"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-3">
                <textbox-vee
                  id="street-city"
                  v-model="newItem.city"
                  placeholder="City"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-3">
                <dropdown-vee
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="street-zip"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode|required"
                  required
                />
              </div>
              <div class="column is-12 has-text-centered">
                <button
                  id="action-btn"
                  type="button"
                  class="button is-secondary"
                  @click="trigerNewItemAction"
                >
                  {{ tableAction }} Real Estate
                </button>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
        <div class="columns">
          <div class="column has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinueCheck('Real Estate')"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import Tooltip from '@/components/phila-ui/Tooltip';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'RealEstate',
  components: {
    CheckboxAdv,
    TextboxVee,
    DropdownVee,
    Tooltip,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      currentFiling: {},
      items: [],
      isSaving: false,
      newItem: {
        locationRealEstate: '',
        nature: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
      },
      nothingToReport: false,
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
          nature: 'Description',
          streetAddress: 'Street Address',
        },
      },
      table: [
        'nature',
        'streetAddress',
        'city',
        'edit',
        'clone',
        'delete',
      ],
    };
  },
  computed: {
    realEstateLocations () {
      return this.controlValueDropdown('relocation');
    },
    states () {
      return this.controlValueDropdown('state');
    },
    realEstateText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('realEstateText');
    },
    realEstateTooltip () {
      return this.$store.getters[ 'admin/activeControlValues' ]('realEstateTooltip');
    },
    realEstateTooltipText () {
      let tooltip = [];
      if (this.currentFiling && this.realEstateTooltip) {
        if (this.currentFiling.stateForm === 'Y') {
          tooltip.push(this.realEstateTooltip[0].description);
        }
        if (this.currentFiling.cityForm === 'Y') {
          tooltip.push(this.realEstateTooltip[1].description);
        }
      }
      return tooltip.join('<br>');
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentRealEstates();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'realEstateText'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'realEstateTooltip'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'relocation'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('realEstate/fetchRealEstate'));
      return Promise.all(promises);
    },
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.realEstateCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentRealEstates () {
      return this.cloneObj(this.$store.state.realEstate.realEstate);
    },
    saveAndContinue () {
      let promises = [];

      const col = 'realEstateCompleted';
      const endpoint = 'real-estate';

      let filing = {
        id: this.currentFiling.id,
      };

      filing[col] = this.currentFiling[col];

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-real-estate'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
  },
};
</script>
